import React, { useMemo } from 'react';
import { flex } from '@fabric-ui/theme';
import useQueryObject from '@hooks/useQueryObjectSql';
import { Box, Skeleton, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { DwQueryRequest } from '../../CustomDashboards/types';
import { calculatePercentageChange, getNumericValue } from '../utils';
import { getAgentsHumanEstimation } from './queries';
import StatusChange from './statusChange';
import { AgentTimeSavedCardProps } from './types';
import { getLast24HoursDate } from './utils';

const QUERY_OPTIONS = { placeholderData: keepPreviousData };

const extractFirstItem = (data: any) => data?.[0];

const AgentTimeSavedCard: React.FC<AgentTimeSavedCardProps> = ({ filterContext, title, unit = '', percentageSub, percentageFormula }) => {
  const queryObject = useMemo(() => [getAgentsHumanEstimation(filterContext) as DwQueryRequest], [filterContext]);

  const last24HoursDate = useMemo(() => getLast24HoursDate(filterContext.dateRange[1]), [filterContext.dateRange]);
  const prev24HoursDate = useMemo(() => getLast24HoursDate(last24HoursDate), [last24HoursDate]);

  const last24HoursQueryObject = useMemo(
    () => [
      getAgentsHumanEstimation({
        ...filterContext,
        dateRange: [last24HoursDate, filterContext.dateRange[1]],
      }) as DwQueryRequest,
    ],
    [filterContext, last24HoursDate]
  );

  const prev24HoursQueryObject = useMemo(
    () => [
      getAgentsHumanEstimation({
        ...filterContext,
        dateRange: [prev24HoursDate, last24HoursDate],
      }) as DwQueryRequest,
    ],
    [filterContext, prev24HoursDate, last24HoursDate]
  );

  const agentHumanEstimation = useQueryObject({
    queryObject,
    options: QUERY_OPTIONS,
    onSuccess: extractFirstItem,
  });

  const agentHumanEstimationInLast24Hours = useQueryObject({
    queryObject: last24HoursQueryObject,
    options: QUERY_OPTIONS,
    onSuccess: extractFirstItem,
  });

  const agentHumanEstimationInPrev24Hours = useQueryObject({
    queryObject: prev24HoursQueryObject,
    options: QUERY_OPTIONS,
    onSuccess: extractFirstItem,
  });

  const currentEstimate = agentHumanEstimation.data?.human_estimate;
  const previousEstimate = agentHumanEstimationInLast24Hours.data?.human_estimate;
  const priorPreviousEstimate = agentHumanEstimationInPrev24Hours.data?.human_estimate;

  const percentage = useMemo(
    () => calculatePercentageChange(getNumericValue(priorPreviousEstimate), getNumericValue(previousEstimate)),
    [previousEstimate, priorPreviousEstimate]
  );

  const isLoading =
    agentHumanEstimation.isLoading ||
    agentHumanEstimation.isRefetching ||
    agentHumanEstimationInLast24Hours.isLoading ||
    agentHumanEstimationInLast24Hours.isRefetching;

  const isFTEMultiplier = title === 'FTE Multiplier';

  return (
    <Box sx={{ padding: '16px 24px', background: 'var(--color-surface-base-primary)', minHeight: '137px', height: '100%', ...flex.col }}>
      <Typography sx={{ fontSize: '14px', marginBottom: '16px', color: 'var(--color-content-base-tertiary)' }} variant="h2">
        {title}
      </Typography>
      {!isLoading ? (
        <div>
          <Box display="flex" flexDirection="row" alignItems="baseline" gap="8px">
            <Typography variant="h3" sx={{ m: 0, fontSize: '24px', color: 'var(--color-content-base-primary)' }}>
              {currentEstimate != null ? percentageFormula(currentEstimate, filterContext.dateRange).toFixed(1) : '0'}
              {isFTEMultiplier ? 'x' : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'var(--color-content-base-primary)' }}>
              {unit}
            </Typography>
          </Box>
          <Box sx={{ ...flex.itemsCenter, gap: 1, lineHeight: '18px', marginTop: '8px' }}>
            <StatusChange
              value={
                isFTEMultiplier ? percentage : percentageFormula(getNumericValue(previousEstimate) - getNumericValue(priorPreviousEstimate))
              }
              toFixed={1}
              showPercentage={isFTEMultiplier}
            />
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'var(--color-content-base-subdued)' }}>
              {percentageSub}
            </Typography>
          </Box>
        </div>
      ) : (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      )}
    </Box>
  );
};

export default AgentTimeSavedCard;
